<template>
  <div class="statistic-table">
    <el-skeleton :loading="activityListStatusInjected === 'pending'" animated>
      <template #template>
        <el-skeleton-item />
      </template>

      <template #default>
        <div class="table">
          <el-table
            :data="data"
            :empty-text="$t('coreNoData')"
            style="width: 100%"
            :border="false"
            :table-layout="$device.isMobile ? 'auto' : 'fixed'"
            class="activity-list"
            :header-cell-style="{ background: 'linear-gradient(180deg, #071821 0%, #040E13 100%) !important' }"
          >
            <el-table-column prop="type" :label="$t('appStatisticTableType')">
              <template #default="scope">
                <span class="capitalize">
                  {{ formatType(scope.row.type) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="value" :label="$t('appStatisticTableValue')">
              <template #default="scope">
                <div class="with-icon">
                  <img
                    :src="
                      tokens[
                        tokenMetaData(blockchain.contracts, scope.row.tokenAddress).name.contract as Tokens
                      ].toString()
                    "
                    loading="lazy"
                    width="32"
                    alt=""
                  />
                  {{ $formatValue(scope.row.value) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="blockTimestamp" :label="$t('appStatisticTableDate')">
              <template #default="scope">
                {{ formatDate(scope.row.blockTimestamp) }}
              </template>
            </el-table-column>

            <el-table-column prop="status" :label="$t('appStatisticTableStatus')">
              <template #default="scope">
                <span
                  class="capitalize"
                  :class="[{ success: scope.row.status === 'SUCCESS' }, { failed: scope.row.status === 'FAILED' }]"
                >
                  {{ getStatusLocalised(scope.row.status) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="blockTimestamp" :label="$t('appStatisticTableTransaction')">
              <template #default="scope">
                <a class="link" :href="txLink(scope.row.transactionHash)" target="_blank">
                  <div class="chain-wrapper">
                    {{ $t('appStatisticExplorer') }}
                    <div class="chain-icon"></div>
                  </div>
                </a>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="tableData?.length && tableData.length > SHORT_LIST_ITEMS_LENGTH" class="controls">
            <SwitcherButton
              :active-text="$t('appStatisticShowMore')"
              :inactive-text="$t('appStatisticShowLess')"
              :change="() => (activityShort = !activityShort)"
            />
          </div>
        </div>
      </template>
    </el-skeleton>
  </div>
</template>

<script setup lang="ts">
import { SHORT_LIST_ITEMS_LENGTH, Tokens } from '~/utils/constants';
import tokens from 'public/img/tokens';
import type { ActivityRecordStatus, TActivitiesResponse } from '~/types/apiService';

const { t } = useI18n();
const { blockchain } = useEnvs();
const activityShort = ref(true);

const { data: activityList } = useNuxtData<TActivitiesResponse>('/v2/activity');
const activityListStatusInjected = inject<Ref<string>>('activityListStatus');

const tableData = asyncComputed(() => {
  const tokenActivities = activityList.value?.items || [];

  return tokenActivities.sort((a, b) => {
    const dateA = new Date(a.blockTimestamp);
    const dateB = new Date(b.blockTimestamp);

    if (dateA < dateB) return 1;
    if (dateA > dateB) return -1;

    if (a.transactionHash > b.transactionHash) return 1;
    if (a.transactionHash < b.transactionHash) return -1;

    return Number(b.value) - Number(a.value);
  });
});

const data = computed(() => {
  return activityShort.value ? tableDataShort.value : tableData.value;
});

function formatType(originalType: string): string {
  const typeForTable = originalType.replace('_', ' ').toLocaleLowerCase();
  let type = '';

  if (typeForTable === 'purchase') type = t('corePurchase');
  else if (typeForTable === 'reward claim') type = t('coreRewardClaim');
  else if (typeForTable === 'special lock') type = t('coreSpecialLock');
  else if (typeForTable === 'pearl sale') type = t('corePearlSale');

  return typeForTable === 'stake unlock' ? t('coreStakeUnlock') : type;
}

function getStatusLocalised(status: ActivityRecordStatus) {
  const statuses = {
    SUCCESS: t('coreStatusSuccess'),
    PENDING: t('coreStatusPending'),
    FAILED: t('coreStatusFailed'),
    CONFIRMING: t('coreStatusConfirming')
  };

  return statuses[status];
}

function formatDate(value: string) {
  if (!Number(Date.parse(value))) {
    return '';
  }
  return new Date(value).toLocaleString('en-GB');
}

const txLink = (value: string): string => {
  return blockchain.blockScanUrl + '/tx/' + value;
};

const tableDataShort = computed(() => {
  return tableData.value?.slice(0, 10) || [];
});
</script>

<style scoped lang="scss">
.table {
  @media screen and (max-width: 767px) {
    margin-left: -16px;
    margin-right: -16px;
  }

  @media screen and (max-width: 479px) {
    margin-left: -38px;
    margin-right: -38px;
    position: static;
    overflow: auto;
    background: var(--blue-3);
    border-top: 1px solid var(--blue-2);
    border-bottom: 1px solid var(--blue-2);
  }
}

.controls {
  display: flex;
  justify-content: center;
  padding: 40px 10px 24px;
  text-align: center;

  @media screen and (max-width: 767px) {
    padding-top: 24px;
    padding-bottom: 24px;
  }
}

.chain-wrapper {
  cursor: pointer;
  display: flex;
  align-items: center;
  grid-column-gap: 7px;

  .chain-icon {
    background-image: url('~/public/img/icons/link.png');
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    width: 20px;
    height: 20px;
    transition: background-image 0.3s ease;
  }

  &:hover {
    .chain-icon {
      background-image: url('~/public/img/icons/link.svg');
    }
  }
}

a.link {
  color: var(--second-text-color);
  transition: 200ms ease all;

  &:hover {
    color: var(--main-text-color);
  }
}

.capitalize {
  text-transform: capitalize;
}

.with-icon {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;

  img {
    margin-left: -8px;
  }
}

.activity-list {
  margin-top: 20px;

  @media screen and (max-width: 479px) {
    margin-top: 0;
  }
}

.success {
  color: var(--confirm-color);
}

.table {
  ::v-deep(.el-table td.el-table__cell .cell) {
    line-height: 24px;
  }
}
</style>

<style>
.statistic-table {
  .el-skeleton {
    height: 370px;

    .el-skeleton__item {
      height: 370px;
      border-radius: 12px;
    }
  }
}
</style>
